import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter } from 'lucide-react';
import azureDevOpsService from '../services/azureDevOpsService';

const TestCaseSearchBar = ({ onSelect, pendingFilter, onClearFilter, placeholder }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); setOpen(false); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await azureDevOpsService.searchTestCases(query);
        setResults(data);
        setOpen(true);
      } catch (e) {
        console.error('Erreur recherche test cases:', e);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = async (result) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setNavigating(true);
    try {
      await onSelect(result);
    } finally {
      setNavigating(false);
    }
  };

  return (
    <div ref={containerRef} className="flex items-center gap-3">
      <div className="relative flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={placeholder || 'Chercher un test case par nom...'}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            disabled={navigating}
          />
          {(loading || navigating) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
        </div>

        {open && results.length > 0 && (
          <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {results.map(r => (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2"
              >
                <span className="text-sm text-gray-800 truncate">{r.title}</span>
                <span className="text-xs text-gray-400 shrink-0">#{r.id}</span>
              </button>
            ))}
          </div>
        )}

        {open && !loading && query.trim().length >= 2 && results.length === 0 && (
          <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm text-gray-500">
            Aucun test case trouvé
          </div>
        )}
      </div>

      {pendingFilter && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium shrink-0">
          <Filter className="w-3.5 h-3.5" />
          <span className="max-w-xs truncate">{pendingFilter}</span>
          {onClearFilter && (
            <button onClick={onClearFilter} className="ml-1 hover:text-blue-600" title="Supprimer le filtre">✕</button>
          )}
        </div>
      )}
    </div>
  );
};

export default TestCaseSearchBar;
